import React from 'react';
import { motion } from 'framer-motion';
import {
  XCircle, CheckCircle, Calendar, Zap, Palette, BarChart3,
  Clock, Smartphone, Wifi, Globe, Camera, Instagram,
  Search, Star, Bell, MessageCircle
} from 'lucide-react';

const Solutions = () => {
  const problems = [
    { icon: Clock, text: 'Takes 3-6 months to launch', color: 'text-red-500' },
    { icon: XCircle, text: 'Costs Rs 50,000 - Rs 2,00,000+', color: 'text-red-500' },
    { icon: XCircle, text: 'Generic WordPress templates', color: 'text-red-500' }
  ];

  const solutions = [
    { icon: Zap, text: 'Live in 48 hours', color: 'text-accent-500' },
    { icon: CheckCircle, text: 'Starting at Rs 9,999/month', color: 'text-accent-500' },
    { icon: Palette, text: 'Custom-designed for your brand', color: 'text-accent-500' }
  ];

  const features = [
    {
      icon: Calendar,
      title: 'Smart Booking Systems',
      items: ['Real-time availability', 'Auto confirmations & reminders', 'WhatsApp integration', 'Zero no-shows'],
      gradient: 'from-primary-500 to-primary-600'
    },
    {
      icon: Zap,
      title: 'Lightning Performance',
      items: ['Loads in <1 second', 'Mobile-optimized', 'Works on slow internet', 'Hosted globally'],
      gradient: 'from-secondary-500 to-secondary-600'
    },
    {
      icon: Palette,
      title: 'Premium Design',
      items: ['Custom brand identity', 'Professional photography', 'Instagram integration', 'Before/after galleries'],
      gradient: 'from-accent-500 to-accent-600'
    },
    {
      icon: BarChart3,
      title: 'Growth Tools Built-In',
      items: ['Google Business integration', 'SEO optimized', 'Analytics dashboard', 'Customer reviews'],
      gradient: 'from-primary-600 to-secondary-500'
    }
  ];

  return (
    <>
      {/* Problem / Solution */}
      <section className="py-20 lg:py-28 bg-white" data-testid="problem-solution-section">
        <div className="container">
          <motion.div
            className="text-center mb-14"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-neutral-900 mb-4">
              Why Traditional Web Agencies{' '}
              <span className="text-secondary-500">Don't Work</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 mb-14">
            {problems.map((p, i) => (
              <motion.div
                key={i}
                className="card-base text-center border-red-100 bg-red-50/50"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                data-testid={`problem-card-${i}`}
              >
                <XCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
                <p className="font-heading font-bold text-neutral-700 text-lg">{p.text}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            className="bg-gradient-to-r from-primary-500 via-primary-600 to-secondary-500 rounded-2xl p-8 md:p-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h3 className="font-heading text-2xl md:text-3xl font-bold text-white text-center mb-8">
              The MakeMyDukaan Way
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              {solutions.map((s, i) => (
                <div
                  key={i}
                  className="text-center bg-white/10 backdrop-blur-sm rounded-xl p-6"
                  data-testid={`solution-card-${i}`}
                >
                  <CheckCircle className="w-10 h-10 text-accent-300 mx-auto mb-3" />
                  <p className="font-heading font-bold text-white text-lg">{s.text}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* What We Build */}
      <section className="py-20 lg:py-28 bg-section-alt" data-testid="what-we-build-section">
        <div className="container">
          <motion.div
            className="text-center mb-14"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-block px-4 py-2 bg-primary-50 text-primary-600 font-medium rounded-full text-sm mb-4">
              Our Capabilities
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-neutral-900">
              What We Build
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, i) => (
              <motion.div
                key={feature.title}
                className="card-base feature-card-glow group"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                data-testid={`feature-card-${i}`}
              >
                <div className={`w-14 h-14 bg-gradient-to-br ${feature.gradient} rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform`}>
                  <feature.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-heading font-bold text-lg text-neutral-900 mb-4">
                  {feature.title}
                </h3>
                <ul className="space-y-2.5">
                  {feature.items.map((item) => (
                    <li key={item} className="flex items-center gap-2 text-neutral-600 text-sm">
                      <CheckCircle className="w-4 h-4 text-accent-500 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Solutions;
